import { ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import useBreakpoint, { breakpoints } from '@root/shared/useBreakpoint';
import { usePopperTooltip } from 'react-popper-tooltip';
import cs from 'classnames';
import s from './PopperTooltip.module.less';
import CustomIcon from '../CustomIcon';

type PopperTooltipProps = {
  triggerElement: ReactNode;
  popperElement: ReactNode;
  triggerElementClassName: string;
};

const PopperTooltip = ({
  triggerElement,
  popperElement,
  triggerElementClassName,
}: PopperTooltipProps) => {
  // When visible is set to undefined, react-popper-tooltip will handle the visibiity state
  const [isVisible, setIsVisible] = useState<boolean | undefined>(undefined);
  const breakpoint = useBreakpoint();

  const handleOnVisibleChange = () => {
    // eslint-disable-next-line max-len
    // Give back control of the visibility state to react-popper-tooltip if the tooltip had been previously closed with the close button
    setIsVisible(undefined);
  };

  const tooltipPlacement = breakpoint === breakpoints.MOBILE ? 'left-end' : 'right-start';
  const { getArrowProps, getTooltipProps, setTriggerRef, setTooltipRef, visible } =
    usePopperTooltip({
      placement: tooltipPlacement,
      delayHide: 200,
      delayShow: 200,
      closeOnOutsideClick: true,
      trigger: 'click',
      visible: isVisible,
      onVisibleChange: handleOnVisibleChange,
      offset: breakpoint !== breakpoints.MOBILE ? [0, 10] : undefined,
    });

  const handleClickTriggerElement = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClickCloseButton = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible(false);
  };

  return (
    <>
      <div
        className={cs(s.popperTooltipTarget, triggerElementClassName, [visible && s.popperVisible])}
        ref={setTriggerRef}
        onClick={handleClickTriggerElement}
        role="button"
      >
        {triggerElement}
      </div>
      {visible &&
        createPortal(
          <div ref={setTooltipRef} {...getTooltipProps({ className: s.tooltipContent })}>
            {/* Using the IconButton component within this component causes
            infintite re-renders */}
            <button
              data-testid="tooltip-close-button"
              type="button"
              className={s.tooltipCloseButton}
              onClick={handleClickCloseButton}
            >
              <CustomIcon type="close" />
            </button>
            <div className={s.popperElementWrapper}>{popperElement}</div>
            <div>
              <div {...getArrowProps({ className: s.tooltipArrow })} />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default PopperTooltip;
